const LOCALE_MAP = { en: 'en-CA', fr: 'fr-CA', es: 'es' };

/**
 * Format a numeric value as currency, respecting the app language.
 * Returns null for invalid or zero/negative amounts (useful for conditional display).
 *
 * @param {number|string|null|undefined} amount
 * @param {object} [options]
 * @param {string} [options.language] - App language key ('en', 'fr', 'es')
 * @param {string} [options.currency='CAD'] - ISO 4217 currency code
 * @param {number} [options.minimumFractionDigits=0]
 * @param {number} [options.maximumFractionDigits=0]
 * @returns {string|null} Formatted currency string or null for invalid/zero amounts
 */
export function formatCurrency(amount, options = {}) {
  const {
    language = 'en',
    currency = 'CAD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
  } = options;

  const num = Number(amount);
  if (!num || !isFinite(num) || num <= 0) return null;

  const locale = LOCALE_MAP[language] || 'en-CA';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  } catch {
    return `$${num.toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
  }
}

/**
 * Format a numeric value as currency, always returning a string (never null).
 * Formats $0 for zero/falsy amounts. Use this for display contexts where a
 * value should always be shown.
 *
 * @param {number|string|null|undefined} amount
 * @param {object} [options] - Same options as formatCurrency
 * @returns {string} Formatted currency string
 */
export function formatCurrencyValue(amount, options = {}) {
  const {
    language = 'en',
    currency = 'CAD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
  } = options;

  const num = Number(amount) || 0;
  const locale = LOCALE_MAP[language] || 'en-CA';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  } catch {
    return `$${num.toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
  }
}

export default formatCurrency;
